<script setup>
/* global axios */
// [AstraChat] Badge de saúde/conexão + acesso rápido ao QR direto no listing de caixas.
// - WhatsApp Cloud oficial: mostra se está conectada (verde) ou precisa reautorizar.
// - Provedores não-oficiais (Channel::Api = AstraCall/Evolution/WAHA): status da sessão
//   (conectado/conectando/desconectado) e, quando não conectado, um botão que abre o
//   QR aqui mesmo (polling do endpoint de qrcode), sem entrar na caixa.
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import { useInboxHealth, loadInboxHealth, statusFor } from './useInboxHealth';

const props = defineProps({
  inbox: { type: Object, required: true },
});
const { t } = useI18n();
const { accountId } = useAccount();
const health = useInboxHealth();

const isApi = computed(() => props.inbox.channel_type === 'Channel::Api');

onMounted(() => {
  if (isApi.value) loadInboxHealth(accountId.value);
});

const instance = computed(() =>
  isApi.value ? health.byInbox[props.inbox.id] : null
);

// { tone: ok|warn|bad|muted, label, qr } — reusa a lógica central de status.
const info = computed(() => statusFor(props.inbox));

const TONE = {
  ok: 'bg-n-teal-3 text-n-teal-11',
  warn: 'bg-n-amber-3 text-n-amber-11',
  bad: 'bg-n-ruby-3 text-n-ruby-11',
  muted: 'bg-n-alpha-2 text-n-slate-11',
};
const DOT = {
  ok: 'bg-n-teal-9',
  warn: 'bg-n-amber-9',
  bad: 'bg-n-ruby-9',
  muted: 'bg-n-slate-8',
};

// ---- QR (polling do endpoint de qrcode da instância) ----
const showQr = ref(false);
const qrImage = ref('');
const qrStatus = ref('');
const qrError = ref('');
const recreating = ref(false);
let timer = null;

const base = () => `/api/v1/accounts/${accountId.value}/kanban/connections`;

// Só provedores cuja sessão pode ser recriada (AstraCall/WAHA) mostram o botão.
const canRecreate = computed(() =>
  ['astracall', 'waha'].includes(instance.value?.provider)
);

// O formato do QR varia por provedor: WAHA/UazAPI mandam data URI pronto; Evolution
// manda base64 PNG puro; AstraCall manda uma STRING (link wa.me / ref 2@...) que
// precisa ser codificada num PNG de QR aqui no cliente (senão vira data URI inválido
// e a imagem não renderiza — era a causa de "não puxa o QR").
const normalizeQr = async raw => {
  if (!raw) return '';
  if (raw.startsWith('data:')) return raw;
  if (/[:@]|wa\.me|linked_devices/.test(raw)) {
    try {
      return await QRCode.toDataURL(raw, { margin: 1, width: 256 });
    } catch {
      return '';
    }
  }
  return `data:image/png;base64,${raw}`;
};

function stopQr() {
  if (timer) clearInterval(timer);
  timer = null;
}

const pollQr = async () => {
  const name = instance.value?.instance_name;
  if (!name) return;
  try {
    const { data } = await axios.get(`${base()}/${name}/qrcode`);
    qrImage.value = await normalizeQr(data.qrcode);
    qrStatus.value = data.status?.state || data.status || '';
    // backend pode responder 200 com { error } (ex.: provedor fora do ar) — sem
    // isso o modal ficava preso em "Gerando código…" sem explicar o motivo.
    qrError.value = qrImage.value ? '' : data.error || '';
    if (/(open|connected)/i.test(qrStatus.value)) {
      stopQr();
      await loadInboxHealth(accountId.value, true);
    }
  } catch (e) {
    qrError.value =
      e?.response?.data?.error || t('KANBAN.connectionHealth.qrError');
  }
};

// Fallback manual: recria a sessão no provedor e recomeça o polling do QR. A
// auto-cura do backend já cobre o caso da sessão apagada, mas isto dá saída ao
// usuário quando o QR não aparece por outro motivo.
const recreateConnection = async () => {
  const name = instance.value?.instance_name;
  if (!name || recreating.value) return;
  recreating.value = true;
  qrError.value = '';
  qrImage.value = '';
  try {
    await axios.post(`${base()}/${name}/recreate`);
    await pollQr();
  } catch (e) {
    qrError.value =
      e?.response?.data?.error || t('KANBAN.connectionHealth.recreateError');
  } finally {
    recreating.value = false;
  }
};

const openQr = () => {
  qrImage.value = '';
  qrError.value = '';
  qrStatus.value = '';
  showQr.value = true;
  pollQr();
  timer = setInterval(pollQr, 3000);
};

const closeQr = () => {
  stopQr();
  showQr.value = false;
};

onBeforeUnmount(stopQr);
</script>

<template>
  <div v-if="info" class="flex items-center gap-2">
    <span
      class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap"
      :class="TONE[info.tone]"
    >
      <span class="size-1.5 rounded-full shrink-0" :class="DOT[info.tone]" />
      {{ info.label }}
    </span>
    <Button
      v-if="info.qr"
      v-tooltip.top="t('KANBAN.connectionHealth.scan')"
      icon="i-ph-qr-code"
      slate
      xs
      faded
      @click="openQr"
    />

    <!-- Modal do QR (acesso rápido pelo listing) -->
    <teleport to="body">
      <div
        v-if="showQr"
        class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
        @click.self="closeQr"
      >
        <div
          class="w-full max-w-sm rounded-xl bg-n-solid-1 border border-n-weak shadow-xl p-6 flex flex-col items-center gap-4"
        >
          <div class="flex items-center justify-between w-full">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ inbox.name }}
            </h3>
            <button
              class="grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
              type="button"
              @click="closeQr"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <p class="text-sm text-n-slate-11 text-center">
            {{ t('KANBAN.connectionHealth.instructions') }}
          </p>

          <div
            class="size-56 grid place-items-center rounded-lg bg-white border border-n-weak overflow-hidden"
          >
            <img
              v-if="qrImage"
              :src="qrImage"
              alt="QR code"
              class="size-full object-contain"
            />
            <span
              v-else-if="qrError"
              class="text-xs text-n-ruby-11 px-4 text-center"
            >
              {{ qrError }}
            </span>
            <span v-else class="text-xs text-n-slate-10">{{
              t('KANBAN.connectionHealth.generating')
            }}</span>
          </div>

          <span v-if="qrStatus" class="text-xs text-n-slate-11 capitalize">
            {{ qrStatus }}
          </span>

          <div
            v-if="canRecreate"
            class="flex flex-col items-center gap-1.5 w-full"
          >
            <p v-if="qrError" class="text-xs text-n-slate-11 text-center">
              {{ t('KANBAN.connectionHealth.sessionHint') }}
            </p>
            <Button
              :label="t('KANBAN.connectionHealth.recreate')"
              icon="i-lucide-refresh-cw"
              slate
              sm
              faded
              :is-loading="recreating"
              :disabled="recreating"
              @click="recreateConnection"
            />
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
